import { useGo, useList, useOne, useCustomMutation, useApiUrl, useDelete } from "@refinedev/core";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Megaphone,
  Users,
  Send,
  CheckCircle,
  XCircle,
  Clock,
  Calendar,
  Loader2,
  Languages,
  Eye,
  SkipForward,
  AlertTriangle,
  Filter,
  Hash,
  Globe,
  Mail,
  Image,
  FileText,
  Video,
  Link2,
  ArrowUpRight,
  Pencil,
  Copy,
  RefreshCw,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { FormHeader } from "@/components/forms";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { Broadcast, BroadcastPreviewResult } from "./types";
import { isBroadcastFilters } from './utils/segment-utils';
import { type SubscriptionPlan, getPlanName } from '../subscription-plans/types';
import { type Course, getCourseTitle } from '../courses/types';
import type { TelegramChannel } from "../telegram-channels/types";
import { CHANNEL_DISPLAY_NAMES, MEDIA_TYPE_DISPLAY_NAMES, PARSE_MODE_DISPLAY_NAMES } from './components/constants';
import { TabDeliveries } from './components/tab-deliveries';

type LanguageRecord = {
  code: string;
  name: string;
  nativeName: string;
};

const STATUS_CONFIG: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; className?: string; icon?: React.ComponentType<{ className?: string }> }> = {
  DRAFT: { label: 'Черновик', variant: 'outline' },
  SCHEDULED: { label: 'Запланирована', variant: 'default', className: 'bg-blue-500/10 text-blue-500 hover:bg-blue-500/20', icon: Clock },
  SENDING: { label: 'Отправка...', variant: 'default', className: 'bg-yellow-500/10 text-yellow-500 hover:bg-yellow-500/20', icon: Send },
  COMPLETED: { label: 'Завершена', variant: 'default', className: 'bg-green-500/10 text-green-500 hover:bg-green-500/20', icon: CheckCircle },
  FAILED: { label: 'Ошибка', variant: 'destructive', icon: XCircle },
  CANCELLED: { label: 'Отменена', variant: 'secondary' },
};

const MEDIA_ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  photo: Image,
  video: Video,
  document: FileText,
};

/**
 * Broadcast show/detail page — comprehensive view with all data
 */
export function BroadcastsShow() {
  const go = useGo();
  const { id } = useParams<{ id: string }>();

  // Preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLanguage, setPreviewLanguage] = useState<string>("");
  const [previewResult, setPreviewResult] = useState<BroadcastPreviewResult | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // Delete dialog state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Confirm send dialog
  const [confirmSendOpen, setConfirmSendOpen] = useState(false);
  const [confirmRecipients, setConfirmRecipients] = useState<Array<{
    id: number;
    firstName: string | null;
    lastName: string | null;
    username: string | null;
    email: string | null;
    telegramId: number | null;
    languagePreference: string;
    isActive: boolean;
  }>>([]);
  const [confirmRecipientsTotal, setConfirmRecipientsTotal] = useState(0);
  const [confirmRecipientsLoading, setConfirmRecipientsLoading] = useState(false);
  const apiUrl = useApiUrl();

  const broadcastResult = useOne<Broadcast>({
    resource: "broadcasts",
    id: Number(id),
  });

  const { mutate: sendBroadcast, mutation: sendMutation } =
    useCustomMutation();
  const { mutate: cancelBroadcast, mutation: cancelMutation } =
    useCustomMutation();
  const { mutate: cloneBroadcast, mutation: cloneMutation } =
    useCustomMutation();
  const { mutate: retryFailedMutation, mutation: retryMutation } =
    useCustomMutation();

  const broadcast = broadcastResult.result;
  const isLoading = broadcastResult.query?.isLoading ?? false;

  // Auto-refresh при статусе SENDING
  const refetchRef = useRef(broadcastResult.query?.refetch);
  refetchRef.current = broadcastResult.query?.refetch;

  useEffect(() => {
    if (broadcast?.status !== 'SENDING') { return; }

    const interval = setInterval(() => {
      refetchRef.current?.();
    }, 5000);

    return () => clearInterval(interval);
  }, [broadcast?.status]);

  const { result: languagesResult } = useList<LanguageRecord>({
    resource: "languages",
    pagination: { pageSize: 100 },
  });
  const { result: plansResult } = useList<SubscriptionPlan>({
    resource: "subscription-plans",
    pagination: { pageSize: 200 },
  });
  const { result: coursesResult } = useList<Course>({
    resource: "courses",
    pagination: { pageSize: 200 },
  });
  const { result: channelsResult } = useList<TelegramChannel>({
    resource: "telegram-channels",
    pagination: { pageSize: 200 },
  });

  const languageMap = useMemo(() => {
    const entries = languagesResult?.data ?? [];
    return new Map(entries.map((item) => [item.code, item.nativeName || item.name]));
  }, [languagesResult?.data]);

  const planMap = useMemo(() => {
    const entries = plansResult?.data ?? [];
    return new Map(entries.map((item) => [item.id, getPlanName(item)]));
  }, [plansResult?.data]);

  const courseMap = useMemo(() => {
    const entries = coursesResult?.data ?? [];
    return new Map(entries.map((item) => [item.id, getCourseTitle(item)]));
  }, [coursesResult?.data]);

  const channelMap = useMemo(() => {
    const entries = channelsResult?.data ?? [];
    return new Map(entries.map((item) => [item.id, item.title]));
  }, [channelsResult?.data]);

  // Загрузка имён конкретных пользователей (userIds)
  const [userNames, setUserNames] = useState<Map<number, string>>(new Map());
  const { mutate: fetchUserNames } = useCustomMutation<{ total: number; users: Array<{ id: number; firstName: string | null; lastName: string | null; username: string | null }> }>();

  useEffect(() => {
    const rawFilters = broadcast?.filters ?? null;
    const legacyFilters = isBroadcastFilters(rawFilters) ? rawFilters : null;
    const userIds = legacyFilters?.userIds;
    if (!userIds || userIds.length === 0) { return; }

    fetchUserNames(
      {
        url: '/admin/broadcasts/recipients-preview',
        method: 'post',
        values: { filters: { userIds }, limit: userIds.length },
      },
      {
        onSuccess: (response) => {
          const users = (response.data as { users: Array<{ id: number; firstName: string | null; lastName: string | null; username: string | null }> }).users;
          const map = new Map<number, string>();
          for (const u of users) {
            const name = [u.firstName, u.lastName].filter(Boolean).join(' ');
            map.set(u.id, name || (u.username ? `@${u.username}` : `#${u.id}`));
          }
          setUserNames(map);
        },
      },
    );
  }, [broadcast?.filters, fetchUserNames]);

  // Get configured translations
  const configuredTranslations = useMemo(() => {
    if (!broadcast?.translations) { return []; }
    return Object.entries(broadcast.translations).map(([code, data]) => ({
      code,
      nativeName: languageMap.get(code) || code,
      messageTemplate: data.messageTemplate,
    }));
  }, [broadcast?.translations, languageMap]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) { return "—"; }
    return new Date(dateString).toLocaleDateString("ru-RU", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatRelativeTime = (dateString: string | null) => {
    if (!dateString) { return null; }
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffMinutes = Math.round(diffMs / (1000 * 60));
    const diffHours = Math.round(diffMs / (1000 * 60 * 60));
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

    if (diffMs < 0) {
      const absDays = Math.abs(diffDays);
      const absHours = Math.abs(diffHours);
      const absMinutes = Math.abs(diffMinutes);
      if (absDays > 0) { return `${absDays} дн. назад`; }
      if (absHours > 0) { return `${absHours} ч. назад`; }
      return `${absMinutes} мин. назад`;
    }
    if (diffDays > 0) { return `через ${diffDays} дн.`; }
    if (diffHours > 0) { return `через ${diffHours} ч.`; }
    return `через ${diffMinutes} мин.`;
  };

  const getStatusBadge = (status: string) => {
    const config = STATUS_CONFIG[status] ?? { label: status, variant: 'outline' as const };
    const IconComponent = config.icon;
    return (
      <Badge variant={config.variant} className={config.className}>
        {IconComponent && <IconComponent className="h-3 w-3 mr-1" />}
        {config.label}
      </Badge>
    );
  };

  const { mutate: fetchPreview } = useCustomMutation<{ data: BroadcastPreviewResult }>();
  const { mutate: deleteBroadcast, mutation: deleteMutation } = useDelete();

  const handleDelete = () => {
    setDeleteError(null);
    deleteBroadcast(
      { resource: 'broadcasts', id: Number(id) },
      {
        onSuccess: () => go({ to: '/broadcasts' }),
        onError: (error) => {
          const message = (error as { message?: string }).message ?? 'Не удалось удалить рассылку';
          setDeleteError(message);
        },
      },
    );
  };

  const handlePreview = (langCode: string) => {
    setPreviewLanguage(langCode);
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewResult(null);

    fetchPreview(
      {
        url: `/admin/broadcasts/${id}/preview`,
        method: 'post',
        values: { languageCode: langCode },
      },
      {
        onSuccess: (response) => {
          setPreviewResult(response.data.data);
          setPreviewLoading(false);
        },
        onError: (error) => {
          setPreviewError(error.message || 'Не удалось получить предпросмотр');
          setPreviewLoading(false);
        },
      }
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!broadcast) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Рассылка не найдена</p>
        <Button variant="link" onClick={() => go({ to: "/broadcasts" })}>
          Вернуться к списку
        </Button>
      </div>
    );
  }

  const totalProcessed = broadcast.sentCount + broadcast.failedCount + (broadcast.skippedCount || 0);
  const progressPercent =
    broadcast.totalRecipients > 0
      ? Math.round((totalProcessed / broadcast.totalRecipients) * 100)
      : 0;

  const canSend = ["DRAFT", "SCHEDULED", "FAILED"].includes(broadcast.status);
  const canCancel = !["COMPLETED", "CANCELLED", "SENDING"].includes(broadcast.status);
  const canDelete = !["SENDING", "SCHEDULED"].includes(broadcast.status);
  const isMutating = sendMutation.isPending || cancelMutation.isPending || cloneMutation.isPending || retryMutation.isPending || deleteMutation.isPending;

  const openConfirmSend = async () => {
    setConfirmSendOpen(true);
    setConfirmRecipientsLoading(true);
    setConfirmRecipients([]);
    setConfirmRecipientsTotal(0);

    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(
        `${apiUrl}/admin/broadcasts/${broadcast.id}/recipients?limit=50`,
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        },
      );
      const json = await response.json();
      if (json.success && json.data) {
        setConfirmRecipients(json.data.users);
        setConfirmRecipientsTotal(json.data.total);
      }
    } catch {
      // Ошибка загрузки — покажем диалог без списка
    } finally {
      setConfirmRecipientsLoading(false);
    }
  };

  const handleSend = () => {
    setConfirmSendOpen(false);
    sendBroadcast(
      {
        url: `/admin/broadcasts/${broadcast.id}/send`,
        method: "post",
        values: {},
      },
      {
        onSuccess: () => broadcastResult.query?.refetch(),
      }
    );
  };

  const handleCancel = () => {
    cancelBroadcast(
      {
        url: `/admin/broadcasts/${broadcast.id}/cancel`,
        method: "post",
        values: {},
      },
      {
        onSuccess: () => broadcastResult.query?.refetch(),
      }
    );
  };

  const handleClone = () => {
    cloneBroadcast(
      {
        url: `/admin/broadcasts/${broadcast.id}/clone`,
        method: 'post',
        values: {},
      },
      {
        onSuccess: (response) => {
          const clonedId = (response.data as { data: { id: number } }).data.id;
          go({ to: `/broadcasts/show/${clonedId}` });
        },
      }
    );
  };

  const handleRetryFailed = () => {
    retryFailedMutation(
      {
        url: `/admin/broadcasts/${broadcast.id}/retry-failed`,
        method: 'post',
        values: {},
      },
      {
        onSuccess: () => broadcastResult.query?.refetch(),
      }
    );
  };

  const legacyFilters = isBroadcastFilters(broadcast.filters) ? broadcast.filters : null;
  const hasFilters = broadcast.filters && Object.keys(broadcast.filters).length > 0;
  const telegramPayload = broadcast.payload?.telegram;
  const hasTranslations = configuredTranslations.length > 0;
  const hasMedia = !!telegramPayload?.media;
  const hasButtons = !!telegramPayload?.buttons && telegramPayload.buttons.length > 0;

  // Count active filter categories
  const activeFiltersCount = (() => {
    if (!broadcast.filters) { return 0; }
    const legacyF = isBroadcastFilters(broadcast.filters) ? broadcast.filters : null;
    if (!legacyF) { return 1; } // SegmentDefinition — считаем как 1 активный фильтр
    let count = 0;
    if (legacyF.isActive !== undefined) { count++; }
    if (legacyF.hasTelegram) { count++; }
    if (legacyF.hasEmail) { count++; }
    if (legacyF.registeredAfter) { count++; }
    if (legacyF.registeredBefore) { count++; }
    if (legacyF.languages?.length) { count++; }
    if (legacyF.productIds?.length) { count++; }
    if (legacyF.courseIds?.length) { count++; }
    if (legacyF.channelIds?.length) { count++; }
    if (legacyF.userIds?.length) { count++; }
    return count;
  })();

  // Duration calculation
  const durationText = (() => {
    if (!broadcast.startedAt || !broadcast.completedAt) { return null; }
    const start = new Date(broadcast.startedAt);
    const end = new Date(broadcast.completedAt);
    const diffMs = end.getTime() - start.getTime();
    const seconds = Math.round(diffMs / 1000);
    if (seconds < 60) { return `${seconds} сек.`; }
    const minutes = Math.floor(seconds / 60);
    const remainSeconds = seconds % 60;
    return `${minutes} мин. ${remainSeconds} сек.`;
  })();

  return (
    <div className="space-y-6">
      {/* Header */}
      <FormHeader
        title={broadcast.name}
        description={`ID: ${broadcast.id} • Создана ${formatDate(broadcast.createdAt)}`}
        onBack={() => go({ to: "/broadcasts" })}
        titleAddon={getStatusBadge(broadcast.status)}
        icon={<Megaphone className="h-5 w-5" />}
        actions={
          <div className="flex items-center gap-2">
            {['DRAFT', 'SCHEDULED'].includes(broadcast.status) && (
              <Button
                variant="outline"
                onClick={() => go({ to: `/broadcasts/edit/${broadcast.id}` })}
              >
                <Pencil className="mr-2 h-4 w-4" />
                Редактировать
              </Button>
            )}
            <Button
              variant="outline"
              onClick={handleClone}
              disabled={isMutating}
            >
              <Copy className="mr-2 h-4 w-4" />
              Копировать
            </Button>
            {broadcast.failedCount > 0 && ['COMPLETED', 'FAILED'].includes(broadcast.status) && (
              <Button
                variant="outline"
                onClick={handleRetryFailed}
                disabled={isMutating}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Повторить ({broadcast.failedCount})
              </Button>
            )}
            {canSend && (
              <Button
                onClick={openConfirmSend}
                disabled={isMutating}
                variant="default"
              >
                <Send className="mr-2 h-4 w-4" />
                Отправить сейчас
              </Button>
            )}
            {canCancel && (
              <Button
                onClick={handleCancel}
                disabled={isMutating}
                variant="outline"
              >
                <XCircle className="mr-2 h-4 w-4" />
                Отменить
              </Button>
            )}
            <Button
              onClick={() => { setDeleteError(null); setDeleteOpen(true); }}
              disabled={isMutating}
              variant="destructive"
              size="icon"
              title={canDelete ? "Удалить рассылку" : "Сначала отмените рассылку"}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        }
        sticky
      />

      {/* Quick Info Bar */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
        <InfoCard
          icon={Hash}
          label="ID"
          value={String(broadcast.id)}
        />
        <InfoCard
          icon={Users}
          label="Получателей"
          value={String(broadcast.totalRecipients)}
        />
        <InfoCard
          icon={Send}
          label="Каналы"
          value={broadcast.channels.map(ch => CHANNEL_DISPLAY_NAMES[ch] ?? ch).join(", ")}
        />
        <InfoCard
          icon={Globe}
          label="Переводы"
          value={hasTranslations ? `${configuredTranslations.length} язык(а)` : "Нет"}
        />
        <InfoCard
          icon={Filter}
          label="Фильтры"
          value={activeFiltersCount > 0 ? `${activeFiltersCount} активных` : "Все пользователи"}
        />
        <InfoCard
          icon={FileText}
          label="Режим"
          value={broadcast.parseMode ? (PARSE_MODE_DISPLAY_NAMES[broadcast.parseMode] ?? broadcast.parseMode) : "Без разметки"}
        />
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">Обзор</TabsTrigger>
          <TabsTrigger value="deliveries">
            Получатели
            {broadcast.totalRecipients > 0 && (
              <Badge variant="secondary" className="ml-2 text-xs">
                {broadcast.totalRecipients.toLocaleString('ru-RU')}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
      {/* Main Content: 2-column layout */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* LEFT: Message + Translations (2/3 width) */}
        <div className="lg:col-span-2 space-y-6">
          {/* Message Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                Сообщение
                {hasTranslations && (
                  <Badge variant="secondary" className="ml-2">
                    <Languages className="h-3 w-3 mr-1" />
                    {configuredTranslations.length} язык(а)
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Default template (always shown) */}
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                  Основной шаблон (fallback)
                </p>
                <div className="bg-muted rounded-lg p-4 whitespace-pre-wrap font-mono text-sm leading-relaxed">
                  {broadcast.messageTemplate || <span className="text-muted-foreground italic">Пустой шаблон</span>}
                </div>
              </div>

              {/* Translations tabs */}
              {hasTranslations && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                    Переводы
                  </p>
                  <Tabs defaultValue={configuredTranslations[0]?.code || "default"}>
                    <TabsList className="flex-wrap h-auto gap-1">
                      {configuredTranslations.map((trans) => (
                        <TabsTrigger key={trans.code} value={trans.code} className="text-xs">
                          {trans.nativeName}
                          <span className="ml-1 text-muted-foreground">({trans.messageTemplate.length})</span>
                        </TabsTrigger>
                      ))}
                    </TabsList>

                    {configuredTranslations.map((trans) => (
                      <TabsContent key={trans.code} value={trans.code} className="mt-3">
                        <div className="bg-muted rounded-lg p-4 whitespace-pre-wrap font-mono text-sm leading-relaxed">
                          {trans.messageTemplate}
                        </div>
                        <div className="mt-3 flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePreview(trans.code)}
                          >
                            <Eye className="mr-2 h-4 w-4" />
                            Предпросмотр для {trans.nativeName}
                          </Button>
                        </div>
                      </TabsContent>
                    ))}
                  </Tabs>
                </div>
              )}

              {!hasTranslations && (
                <div className="rounded-lg border border-yellow-200 bg-yellow-50 dark:border-yellow-900 dark:bg-yellow-950/50 p-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-400 mt-0.5 shrink-0" />
                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                      Переводы не настроены. Все пользователи получат сообщение из основного шаблона.
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Telegram Payload Card */}
          {(hasMedia || hasButtons) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  Telegram вложения
                  {hasMedia && telegramPayload?.media?.type && <Badge variant="outline">{MEDIA_TYPE_DISPLAY_NAMES[telegramPayload.media.type] ?? telegramPayload.media.type}</Badge>}
                  {hasButtons && <Badge variant="outline">{telegramPayload?.buttons?.length} кнопок</Badge>}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {hasMedia && (
                  <div className="rounded-lg border p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      {(() => {
                        const mediaType = telegramPayload?.media?.type;
                        const MediaIcon = (mediaType ? MEDIA_ICON_MAP[mediaType] : undefined) ?? FileText;
                        return <MediaIcon className="h-5 w-5 text-muted-foreground" />;
                      })()}
                      <span className="font-medium">
                        {telegramPayload?.media?.type ? (MEDIA_TYPE_DISPLAY_NAMES[telegramPayload.media.type] ?? telegramPayload.media.type) : null}
                      </span>
                      {telegramPayload?.media?.parseMode && (
                        <Badge variant="outline" className="text-xs">
                          {telegramPayload?.media?.parseMode}
                        </Badge>
                      )}
                    </div>
                    <div className="text-sm">
                      <span className="text-muted-foreground">URL: </span>
                      <a
                        href={telegramPayload?.media?.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:underline break-all"
                      >
                        {telegramPayload?.media?.url}
                        <ArrowUpRight className="inline h-3 w-3 ml-0.5" />
                      </a>
                    </div>
                    {telegramPayload?.media?.caption && (
                      <div className="text-sm">
                        <span className="text-muted-foreground">Подпись: </span>
                        <span className="whitespace-pre-wrap">{telegramPayload?.media?.caption}</span>
                      </div>
                    )}
                  </div>
                )}

                {hasButtons && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Inline-кнопки
                    </p>
                    <div className="space-y-2">
                      {telegramPayload?.buttons?.map((button) => (
                        <div
                          key={`btn-${button.text}-${button.url ?? button.callbackData ?? ''}`}
                          className="flex items-center gap-3 rounded-lg border p-3"
                        >
                          <Link2 className="h-4 w-4 text-muted-foreground shrink-0" />
                          <div className="flex-1 min-w-0">
                            <span className="font-medium text-sm">{button.text}</span>
                            {button.url && (
                              <p className="text-xs text-muted-foreground truncate">
                                {button.url}
                              </p>
                            )}
                            {button.callbackData && (
                              <p className="text-xs text-muted-foreground font-mono">
                                callback: {button.callbackData}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Filters Card */}
          {hasFilters && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Filter className="h-5 w-5" />
                  Фильтры получателей
                  <Badge variant="secondary">{activeFiltersCount} активных</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3 sm:grid-cols-2">
                  {legacyFilters?.isActive !== undefined && (
                    <FilterItem label="Статус" value={legacyFilters.isActive ? "Активные" : "Неактивные"} />
                  )}
                  {legacyFilters?.hasTelegram && (
                    <FilterItem label="Telegram" value="Есть аккаунт" />
                  )}
                  {legacyFilters?.hasEmail && (
                    <FilterItem label="Email" value="Есть email" icon={Mail} />
                  )}
                  {legacyFilters?.registeredAfter && (
                    <FilterItem label="Зарег. после" value={legacyFilters.registeredAfter} icon={Calendar} />
                  )}
                  {legacyFilters?.registeredBefore && (
                    <FilterItem label="Зарег. до" value={legacyFilters.registeredBefore} icon={Calendar} />
                  )}
                  {legacyFilters?.languages && legacyFilters.languages.length > 0 && (
                    <FilterItem
                      label="Языки"
                      value={legacyFilters.languages.map((code) => languageMap.get(code) ?? code).join(", ")}
                      icon={Globe}
                    />
                  )}
                  {legacyFilters?.productIds && legacyFilters.productIds.length > 0 && (
                    <FilterItem
                      label="Абонементы"
                      value={legacyFilters.productIds.map((planId) => planMap.get(Number(planId)) ?? planId).join(", ")}
                    />
                  )}
                  {legacyFilters?.courseIds && legacyFilters.courseIds.length > 0 && (
                    <FilterItem
                      label="Курсы"
                      value={legacyFilters.courseIds.map((courseId) => courseMap.get(courseId) ?? courseId).join(", ")}
                    />
                  )}
                  {legacyFilters?.channelIds && legacyFilters.channelIds.length > 0 && (
                    <FilterItem
                      label="Каналы"
                      value={legacyFilters.channelIds.map((channelId) => channelMap.get(channelId) ?? channelId).join(", ")}
                    />
                  )}
                  {legacyFilters?.userIds && legacyFilters.userIds.length > 0 && (
                    <FilterItem
                      label="Конкретные пользователи"
                      value={legacyFilters.userIds
                        .slice(0, 5)
                        .map((uid) => userNames.get(uid) ?? `#${uid}`)
                        .join(', ') + (legacyFilters.userIds.length > 5 ? ` и ещё ${legacyFilters.userIds.length - 5}` : '')}
                      icon={Users}
                    />
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* RIGHT: Statistics + Timeline (1/3 width) */}
        <div className="space-y-6">
          {/* Statistics Card */}
          <Card>
            <CardHeader>
              <CardTitle>Статистика отправки</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Total recipients */}
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-muted">
                  <Users className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Получателей</p>
                  <p className="text-3xl font-bold tabular-nums">
                    {broadcast.totalRecipients.toLocaleString('ru-RU')}
                  </p>
                </div>
              </div>

              {/* Progress bar */}
              {broadcast.status !== "DRAFT" && (
                <>
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-muted-foreground">Прогресс</span>
                      <span className="font-medium tabular-nums">{progressPercent}%</span>
                    </div>
                    <Progress value={progressPercent} className="h-2" />
                    {broadcast.status === 'SENDING' && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Обработано {totalProcessed.toLocaleString('ru-RU')} из {broadcast.totalRecipients.toLocaleString('ru-RU')}
                      </p>
                    )}
                  </div>

                  {/* Stats grid */}
                  <div className="grid grid-cols-2 gap-3">
                    <StatItem
                      icon={CheckCircle}
                      iconClassName="text-green-500"
                      label="Отправлено"
                      value={broadcast.sentCount}
                      total={broadcast.totalRecipients}
                    />
                    <StatItem
                      icon={XCircle}
                      iconClassName="text-destructive"
                      label="Ошибок"
                      value={broadcast.failedCount}
                      total={broadcast.totalRecipients}
                    />
                    {(broadcast.skippedCount ?? 0) > 0 && (
                      <StatItem
                        icon={SkipForward}
                        iconClassName="text-orange-500"
                        label="Пропущено"
                        value={broadcast.skippedCount ?? 0}
                        total={broadcast.totalRecipients}
                        tooltip="Пользователи без перевода на их язык получили fallback-сообщение"
                      />
                    )}
                  </div>

                  {/* Success rate */}
                  {broadcast.status === 'COMPLETED' && broadcast.totalRecipients > 0 && (
                    <div className="rounded-lg bg-muted p-3">
                      <p className="text-xs text-muted-foreground">Процент доставки</p>
                      <p className="text-2xl font-bold tabular-nums">
                        {Math.round((broadcast.sentCount / broadcast.totalRecipients) * 100)}%
                      </p>
                    </div>
                  )}

                  {/* Duration */}
                  {durationText && (
                    <div className="rounded-lg bg-muted p-3">
                      <p className="text-xs text-muted-foreground">Время отправки</p>
                      <p className="text-lg font-semibold">{durationText}</p>
                    </div>
                  )}
                </>
              )}

              {broadcast.status === 'DRAFT' && (
                <div className="rounded-lg border-dashed border-2 p-4 text-center">
                  <p className="text-sm text-muted-foreground">
                    Рассылка ещё не отправлялась
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Timeline Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Таймлайн
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative space-y-0">
                <TimelineItem
                  icon={Calendar}
                  label="Создана"
                  date={formatDate(broadcast.createdAt)}
                  active
                />
                {broadcast.scheduledAt && (
                  <TimelineItem
                    icon={Clock}
                    label="Запланирована"
                    date={formatDate(broadcast.scheduledAt)}
                    hint={formatRelativeTime(broadcast.scheduledAt)}
                    active
                  />
                )}
                <TimelineItem
                  icon={Send}
                  label="Начата"
                  date={broadcast.startedAt ? formatDate(broadcast.startedAt) : null}
                  active={!!broadcast.startedAt}
                />
                <TimelineItem
                  icon={broadcast.status === 'CANCELLED' ? XCircle : CheckCircle}
                  label={broadcast.status === 'CANCELLED' ? 'Отменена' : 'Завершена'}
                  date={broadcast.completedAt ? formatDate(broadcast.completedAt) : null}
                  active={!!broadcast.completedAt}
                  isLast
                />
              </div>
            </CardContent>
          </Card>

          {/* Technical Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Техническая информация</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <TechRow label="ID" value={String(broadcast.id)} />
              <TechRow label="Статус" value={broadcast.status} />
              <TechRow label="parseMode" value={broadcast.parseMode ?? "null (plain text)"} />
              <TechRow label="Каналы" value={broadcast.channels.join(", ")} />
              <TechRow label="Шаблон" value={`${broadcast.messageTemplate.length} символов`} />
              <TechRow label="Переводов" value={String(configuredTranslations.length)} />
              <TechRow label="Фильтров" value={String(activeFiltersCount)} />
              <TechRow label="Вложения" value={hasMedia ? "Да" : "Нет"} />
              <TechRow label="Кнопки" value={hasButtons ? String(telegramPayload?.buttons?.length) : "Нет"} />
              <TechRow label="Обновлена" value={formatDate(broadcast.updatedAt)} />
            </CardContent>
          </Card>
        </div>
      </div>

        </TabsContent>

        <TabsContent value="deliveries">
          <TabDeliveries broadcastId={broadcast.id} broadcastStatus={broadcast.status} />
        </TabsContent>
      </Tabs>
      {/* Confirm Send Dialog */}
      <Dialog open={confirmSendOpen} onOpenChange={setConfirmSendOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Подтвердите отправку</DialogTitle>
            <DialogDescription>
              Рассылка &laquo;{broadcast.name}&raquo; будет отправлена.
              Это действие нельзя отменить после начала.
            </DialogDescription>
          </DialogHeader>

          {/* Список получателей с бэкенда */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">
                Получателей:{' '}
                {confirmRecipientsLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin inline" />
                ) : (
                  <span className="text-foreground">{confirmRecipientsTotal.toLocaleString('ru-RU')}</span>
                )}
              </span>
            </div>

            {confirmRecipientsLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : confirmRecipients.length > 0 ? (
              <div className="max-h-64 overflow-y-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-background">
                    <tr className="border-b text-left">
                      <th className="px-3 py-2 font-medium text-muted-foreground">ID</th>
                      <th className="px-3 py-2 font-medium text-muted-foreground">Имя</th>
                      <th className="px-3 py-2 font-medium text-muted-foreground">Username</th>
                      <th className="px-3 py-2 font-medium text-muted-foreground">Telegram</th>
                    </tr>
                  </thead>
                  <tbody>
                    {confirmRecipients.map((u) => (
                      <tr key={u.id} className="border-b last:border-0">
                        <td className="px-3 py-1.5 tabular-nums">{u.id}</td>
                        <td className="px-3 py-1.5">
                          {[u.firstName, u.lastName].filter(Boolean).join(' ') || `#${u.id}`}
                        </td>
                        <td className="px-3 py-1.5 text-muted-foreground">
                          {u.username ? `@${u.username}` : '—'}
                        </td>
                        <td className="px-3 py-1.5">
                          {u.telegramId ? (
                            <Badge variant="secondary" className="text-xs">есть</Badge>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {confirmRecipientsTotal > confirmRecipients.length && (
                  <p className="text-xs text-muted-foreground text-center py-2">
                    Показано {confirmRecipients.length} из {confirmRecipientsTotal.toLocaleString('ru-RU')}
                  </p>
                )}
              </div>
            ) : (
              <div className="rounded-md border p-4 text-center">
                <p className="text-sm text-muted-foreground">Нет получателей, подходящих под фильтры</p>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setConfirmSendOpen(false)}>
              Отмена
            </Button>
            <Button
              onClick={handleSend}
              disabled={isMutating || confirmRecipientsLoading || confirmRecipientsTotal === 0}
            >
              <Send className="mr-2 h-4 w-4" />
              Отправить ({confirmRecipientsTotal.toLocaleString('ru-RU')})
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Удалить рассылку?</DialogTitle>
            <DialogDescription>
              Рассылка &laquo;{broadcast.name}&raquo; будет удалена безвозвратно вместе со всей историей доставки.
              {!canDelete && (
                <span className="block mt-2 text-destructive font-medium">
                  Нельзя удалить рассылку в статусе {broadcast.status}. Сначала отмените её.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          {deleteError && (
            <div className="rounded-lg border border-destructive bg-destructive/10 p-3">
              <p className="text-sm text-destructive">{deleteError}</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)} disabled={deleteMutation.isPending}>
              Отмена
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={!canDelete || deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Удаление...</>
              ) : (
                <><Trash2 className="mr-2 h-4 w-4" />Удалить</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Предпросмотр сообщения</DialogTitle>
            <DialogDescription>
              Язык: {languageMap.get(previewLanguage) || previewLanguage}
            </DialogDescription>
          </DialogHeader>

          {previewLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          )}

          {previewError && (
            <div className="rounded-lg border border-destructive bg-destructive/10 p-4">
              <p className="text-sm text-destructive">{previewError}</p>
            </div>
          )}

          {previewResult && (
            <div className="space-y-4">
              {/* Telegram-style message bubble */}
              <div className="bg-[#e8f5e9] dark:bg-[#1b3a26] rounded-xl overflow-hidden">
                <div className="px-3 pt-2 pb-1">
                  <span className="text-xs font-semibold text-[#2e7d32] dark:text-[#81c784]">
                    Бот рассылки
                  </span>
                </div>
                <div className="px-3 pb-3 whitespace-pre-wrap text-sm">
                  {previewResult.message}
                </div>
              </div>

              {/* Preview stats */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="rounded-lg bg-muted p-3">
                  <span className="text-muted-foreground">Получателей:</span>
                  <p className="font-bold text-lg">{previewResult.recipientsCount.toLocaleString('ru-RU')}</p>
                </div>
                <div className="rounded-lg bg-muted p-3">
                  <span className="text-muted-foreground">Язык:</span>
                  <p className="font-bold text-lg">{languageMap.get(previewResult.language) || previewResult.language}</p>
                </div>
              </div>

              {/* Sample user */}
              {previewResult.sampleUser && (
                <div className="rounded-lg border p-4">
                  <h4 className="text-sm font-medium mb-3">Пример пользователя:</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <PreviewField label="Имя" value={previewResult.sampleUser.displayName} />
                    <PreviewField label="Username" value={previewResult.sampleUser.username} />
                    <PreviewField label="Email" value={previewResult.sampleUser.email} />
                    <PreviewField label="Подписка" value={previewResult.sampleUser.subscriptionStatus} />
                    {previewResult.sampleUser.subscriptionPlanName && (
                      <PreviewField label="План" value={previewResult.sampleUser.subscriptionPlanName} />
                    )}
                    {previewResult.sampleUser.daysUntilExpiry !== null && (
                      <PreviewField label="Дней до истечения" value={String(previewResult.sampleUser.daysUntilExpiry)} />
                    )}
                    <PreviewField label="Заказов" value={String(previewResult.sampleUser.ordersCount)} />
                    <PreviewField label="Потрачено" value={`${previewResult.sampleUser.totalSpent.toLocaleString('ru-RU')} ₸`} />
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Helper Components ─────────────────────────────────────────────

function InfoCard({ icon: Icon, label, value }: { icon: React.ComponentType<{ className?: string }>; label: string; value: string }) {
  return (
    <div className="rounded-lg border bg-card p-3 space-y-1">
      <div className="flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
      <p className="text-sm font-medium truncate">{value}</p>
    </div>
  );
}

function StatItem({
  icon: Icon,
  iconClassName,
  label,
  value,
  total,
  tooltip,
}: {
  icon: React.ComponentType<{ className?: string }>;
  iconClassName: string;
  label: string;
  value: number;
  total: number;
  tooltip?: string;
}) {
  const percent = total > 0 ? Math.round((value / total) * 100) : 0;
  const content = (
    <div className="flex items-center gap-2 rounded-lg bg-muted p-2.5">
      <Icon className={`h-4 w-4 shrink-0 ${iconClassName}`} />
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="font-bold tabular-nums">{value.toLocaleString('ru-RU')}</p>
        {percent > 0 && <p className="text-xs text-muted-foreground">{percent}%</p>}
      </div>
    </div>
  );

  if (tooltip) {
    return <div title={tooltip}>{content}</div>;
  }

  return content;
}

function TimelineItem({
  icon: Icon,
  label,
  date,
  hint,
  active,
  isLast,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  date: string | null;
  hint?: string | null;
  active: boolean;
  isLast?: boolean;
}) {
  return (
    <div className="flex gap-3 pb-4 last:pb-0">
      <div className="flex flex-col items-center">
        <div className={`p-1.5 rounded-full ${active ? 'bg-primary/10' : 'bg-muted'}`}>
          <Icon className={`h-3.5 w-3.5 ${active ? 'text-primary' : 'text-muted-foreground'}`} />
        </div>
        {!isLast && (
          <div className={`w-px flex-1 min-h-[16px] ${active ? 'bg-primary/20' : 'bg-border'}`} />
        )}
      </div>
      <div className="pb-1">
        <p className={`text-sm font-medium ${active ? '' : 'text-muted-foreground'}`}>{label}</p>
        {date ? (
          <p className="text-xs text-muted-foreground">{date}</p>
        ) : (
          <p className="text-xs text-muted-foreground/50">—</p>
        )}
        {hint && <p className="text-xs text-blue-500">{hint}</p>}
      </div>
    </div>
  );
}

function FilterItem({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: string;
  icon?: React.ComponentType<{ className?: string }>;
}) {
  return (
    <div className="flex items-start gap-2 rounded-lg border p-3">
      {Icon && <Icon className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />}
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm font-medium break-words">{value}</p>
      </div>
    </div>
  );
}

function TechRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between items-baseline gap-2">
      <span className="text-muted-foreground shrink-0">{label}:</span>
      <span className="font-mono text-xs text-right truncate">{value}</span>
    </div>
  );
}

function PreviewField({ label, value }: { label: string; value: string | null }) {
  if (!value) { return null; }
  return (
    <div>
      <span className="text-muted-foreground">{label}:</span>
      <span className="ml-2">{value}</span>
    </div>
  );
}
